import { useCallback, useEffect, useState } from 'react';
import useAuthentication from '~/hooks/use-authentication';
import useDebounce from '~/hooks/use-debounce';
import useNotification from '~/hooks/use-notification';
import api from '~/lib/api';
import useIpxProviders from './use-ipx-providers';
import usePagination from './use-pagination';

function useNetworkService() {
  const [file, setFile] = useState();
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [error, setError] = useState();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const { data: ipxProviders } = useIpxProviders();
  const [ipxProvider, setIpxProvider] = useState();
  const debouncedSearch = useDebounce(search);
  const pagination = usePagination();
  const { addNotification } = useNotification();
  const { isLoggedIn, isGod, isDemiGod } = useAuthentication();

  const isAllowedAccess = isGod || isDemiGod;

  const get = useCallback(async () => {
    if (!isLoggedIn) return;
    if (!isAllowedAccess) return;
    if (!ipxProvider) return;
    try {
      const response = await api.get(`/networks/ipx-provider`, {
        params: {
          ipxProvider,
          search,
          limit: pagination.itemsPerPage,
          offset: pagination.getOffset(),
        },
      });
      pagination.setTotalItems(response.data.total);
      setData(response.data.data);
      setLoading(false);
    } catch (e) {
      setError(e);
      setLoading(false);
    }
  }, [
    isLoggedIn,
    isAllowedAccess,
    ipxProvider,
    debouncedSearch,
    pagination.currentPage,
    pagination.itemsPerPage,
    ipxProviders,
  ]);

  async function uploadFile() {
    setError(null);
    setIpxProvider(null);
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('data', file);
      const response = await api.post(`/networks/network-list`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      pagination.setTotalItems(response.data.total);
      setData(response.data.data);
      setIpxProvider(response.data.ipxProvider);
      addNotification({
        type: 'success',
        message: 'Network list uploaded successfully',
      });
    } catch (e) {
      setError(e);
    }
    setUploadLoading(false);
  }

  function unsetFile() {
    setFile();
  }

  useEffect(() => {
    setError(null);
    get();
  }, [get, ipxProvider]);

  useEffect(() => {
    setIpxProvider(ipxProviders?.[0].name);
    get();
  }, [ipxProviders]);

  return {
    networkUploadManager: {
      file,
      loading: uploadLoading,
      setFile,
      uploadFile,
      unsetFile,
    },
    get,
    error,
    data,
    pagination,
    ipxProvider,
    setIpxProvider,
    search: {
      value: search,
      onChange(e) {
        pagination.resetPages();
        setSearch(e.target.value);
        setLoading(true);
      },
      loading,
    },
  };
}

export default useNetworkService;
